import { isBookingOpen, isWaitlistOnly } from "@/i18n/config"
import { isMembershipFirstSite } from "@/lib/membership-first"

/**
 * What the site-wide top banner says and where it points, per market.
 *
 * A waitlist market's banner announces the launch city ("Binnenkort live in
 * Amsterdam"); once its check-up books, the same banner has to say where you
 * can actually go. Two key sets rather than one, so the copy turns over with
 * `<KEY>_STATUS` instead of needing a copy PR timed to the flip.
 *
 * Only the waitlist markets carry `bannerWaitlist*` with anything meaningful.
 * CH and DE never read them — every catalog holds the keys because `en-CH` is
 * the type source for `t()` and `lint:mirror-locales` requires parity.
 */
const WAITLIST_BANNER_KEYS = {
  message: "bannerWaitlist",
  messageShort: "bannerWaitlistShort",
  link: "bannerWaitlistLink",
} as const

const LIVE_BANNER_KEYS = {
  message: "bannerLive",
  messageShort: "bannerLiveShort",
  link: "bannerLiveLink",
} as const

export type TopBannerKeys =
  | typeof WAITLIST_BANNER_KEYS
  | typeof LIVE_BANNER_KEYS

/** The `ui` namespace keys the banner reads for this market. */
export function topBannerKeys(country: string): TopBannerKeys {
  return isWaitlistOnly(country) ? WAITLIST_BANNER_KEYS : LIVE_BANNER_KEYS
}

/**
 * The page the banner links to when it isn't opening the waitlist modal.
 *
 * Membership is Swiss-only for now, so a market that cannot sell it points at
 * its clinics instead — NL would otherwise link to a page it has no product
 * for the moment it leaves waitlist mode.
 */
export function topBannerLinkPage(country: string): "membership" | "locations" {
  return isBookingOpen(country, "membership") ? "membership" : "locations"
}

/**
 * Whether the market shows the top banner at all.
 *
 * Germany runs without it: its only content was the Selbstzahler notice, which
 * the Kostenübernahme page already states. The membership-first site runs
 * without it too: its "Join Ahead membership" promo is what the whole site now
 * says, and the membership page it links to is gone.
 */
export function showsTopBanner(country: string): boolean {
  return country !== "de" && !isMembershipFirstSite(country)
}
